import { createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, patch, post, put } from "helpers/api_helper"
import {
  TENANT_GET_INTEGRATION_CATEGORIES,
  TENANT_GET_INTEGRATIONS_CATALOG_TREE,
  TENANT_GET_AVAILABLE_INTEGRATIONS,
  TENANT_GET_MY_INTEGRATIONS,
  TENANT_GET_MY_INTEGRATION_DETAILS,
  TENANT_CONNECT_INTEGRATION,
  TENANT_UPDATE_MY_INTEGRATION,
  TENANT_DISCONNECT_MY_INTEGRATION,
  TENANT_DELETE_MY_INTEGRATION,
} from "helpers/url_helper"

const requireId = (payload, actionName) => {
  const id = payload && (payload.id || payload._id)
  if (!id) {
    throw new Error(`${actionName} requires payload.id`)
  }
  return id
}

export const tenantGetIntegrationCategories = createAsyncThunk(
  TENANT_GET_INTEGRATION_CATEGORIES,
  async () => {
    const response = await get(TENANT_GET_INTEGRATION_CATEGORIES)
    return response
  }
)

export const tenantGetIntegrationsCatalogTree = createAsyncThunk(
  TENANT_GET_INTEGRATIONS_CATALOG_TREE,
  async () => {
    const response = await get(TENANT_GET_INTEGRATIONS_CATALOG_TREE)
    return response
  }
)

export const tenantGetAvailableIntegrations = createAsyncThunk(
  TENANT_GET_AVAILABLE_INTEGRATIONS,
  async payload => {
    const response = await get(TENANT_GET_AVAILABLE_INTEGRATIONS, {
      params: payload || {},
    })
    return response
  }
)

export const tenantGetMyIntegrations = createAsyncThunk(
  TENANT_GET_MY_INTEGRATIONS,
  async payload => {
    const response = await get(TENANT_GET_MY_INTEGRATIONS, {
      params: payload || {},
    })
    return response
  }
)

export const tenantGetMyIntegrationDetails = createAsyncThunk(
  TENANT_GET_MY_INTEGRATION_DETAILS,
  async payload => {
    const id = requireId(payload, "TENANT_GET_MY_INTEGRATION_DETAILS")
    const stepUpToken = payload && payload.stepUpToken
    const config = stepUpToken
      ? { params: { stepUpToken } }
      : {}
    const response = await get(`admin/integrations/my/${id}`, config)
    return response
  }
)

export const tenantConnectIntegration = createAsyncThunk(
  TENANT_CONNECT_INTEGRATION,
  async payload => {
    const response = await post(TENANT_CONNECT_INTEGRATION, payload)
    return response
  }
)

export const tenantUpdateMyIntegration = createAsyncThunk(
  TENANT_UPDATE_MY_INTEGRATION,
  async payload => {
    const id = requireId(payload, "TENANT_UPDATE_MY_INTEGRATION")
    const safePayload = payload || {}
    const { id: _omitId, _id: _omitUnderscoreId, ...body } = safePayload
    const response = await put(`admin/integrations/my/${id}/update`, body)
    return response
  }
)

export const tenantDisconnectMyIntegration = createAsyncThunk(
  TENANT_DISCONNECT_MY_INTEGRATION,
  async payload => {
    const id = requireId(payload, "TENANT_DISCONNECT_MY_INTEGRATION")
    const token = payload && payload.stepUpToken
    const body = token ? { stepUpToken: token } : {}
    const response = await patch(
      `admin/integrations/my/${id}/disconnect`,
      body
    )
    return response
  }
)

export const tenantDeleteMyIntegration = createAsyncThunk(
  TENANT_DELETE_MY_INTEGRATION,
  async payload => {
    const id = requireId(payload, "TENANT_DELETE_MY_INTEGRATION")
    const token = payload && payload.stepUpToken
    const qs = token
      ? `?stepUpToken=${encodeURIComponent(token)}`
      : ""
    const response = await del(`admin/integrations/my/${id}/delete${qs}`, {})
    return response
  }
)

/** Step-up: request email OTP to view unmasked credentials (Flow A) */
export const tenantRequestIntegrationStepUpView = createAsyncThunk(
  "tenantIntegration/requestStepUpView",
  async payload => {
    const id = requireId(payload, "tenantRequestIntegrationStepUpView")
    const response = await post(`admin/integrations/my/${id}/step-up/view`, {})
    return response
  }
)

/** Step-up: request email OTP before editing / PUT update (Flow B) */
export const tenantRequestIntegrationStepUpEdit = createAsyncThunk(
  "tenantIntegration/requestStepUpEdit",
  async payload => {
    const id = requireId(payload, "tenantRequestIntegrationStepUpEdit")
    const response = await post(`admin/integrations/my/${id}/step-up/edit`, {})
    return response
  }
)

/** Step-up: request OTP before PATCH disconnect (Flow C) */
export const tenantRequestIntegrationStepUpDisconnect = createAsyncThunk(
  "tenantIntegration/requestStepUpDisconnect",
  async payload => {
    const id = requireId(payload, "tenantRequestIntegrationStepUpDisconnect")
    const response = await post(
      `admin/integrations/my/${id}/step-up/disconnect`,
      {}
    )
    return response
  }
)

/** Step-up: request OTP before DELETE (Flow D) */
export const tenantRequestIntegrationStepUpDelete = createAsyncThunk(
  "tenantIntegration/requestStepUpDelete",
  async payload => {
    const id = requireId(payload, "tenantRequestIntegrationStepUpDelete")
    const response = await post(`admin/integrations/my/${id}/step-up/delete`, {})
    return response
  }
)

/** Step-up: verify 6-digit OTP; returns `data.stepUpToken` and `data.integrationAction` */
export const tenantVerifyIntegrationStepUp = createAsyncThunk(
  "tenantIntegration/verifyStepUp",
  async payload => {
    const id = requireId(payload, "tenantVerifyIntegrationStepUp")
    const otp = payload && payload.otp
    const response = await post(
      `admin/integrations/my/${id}/step-up/verify`,
      { otp }
    )
    return response
  }
)

/** Send a dummy test SMS through the connected SMS integration flow */
export const tenantTestSmsIntegration = createAsyncThunk(
  "tenantIntegration/testSms",
  async payload => {
    const id = requireId(payload, "tenantTestSmsIntegration")
    const phoneNumber = payload && payload.phoneNumber
    const response = await post(`admin/integrations/my/${id}/test-sms`, {
      phoneNumber,
    })
    return response
  }
)
